import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, MessageCircle, Timer } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { generateCode, login, fetchUserShops, clearError } from './authSlice';
import { Alert, Button, IconButton, Input, OtpInput } from '@/components/ui';
import { Splash } from '@/components/layout/Splash';
import { validatePhone, validateAuthCode } from '@/utils/validators';
import { formatPhone } from '@/utils/formatters';
import { APP_VERSION, SUPPORT_WHATSAPP } from '@/utils/constants';
import { useI18n } from '@/hooks/useI18n.ts';

/**
 * 01 Masuk / 02 Verifikasi — T1 brand split. Phone: plum hero + bottom form card.
 * Landscape: message column | 540px form card. Two-step phone → OTP (auto-submit at 6 digits).
 */
const COUNTRY_CODES = ['+62', '+60', '+65'];
const mmss = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

const LoginPage: React.FC = () => {
    const { t, changeLanguage, isIndonesian } = useI18n();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { isLoading, error, isAuthenticated, isInitialized } = useAppSelector((state) => state.auth);

    const [step, setStep] = useState<'phone' | 'code'>('phone');
    const [phone, setPhone] = useState('');
    const [countryCode, setCountryCode] = useState('+62');
    const [resendIn, setResendIn] = useState(0);
    const [authCode, setAuthCode] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [codeError, setCodeError] = useState('');

    useEffect(() => {
        if (isInitialized && isAuthenticated) navigate('/shop-select');
    }, [isAuthenticated, isInitialized, navigate]);

    useEffect(() => {
        if (resendIn <= 0) return;
        const id = setTimeout(() => setResendIn((s) => s - 1), 1000);
        return () => clearTimeout(id);
    }, [resendIn]);

    // Auto-submit once 6 digits are entered
    useEffect(() => {
        if (step === 'code' && authCode.length === 6 && !isLoading) submitLogin();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authCode]);

    if (!isInitialized) return <Splash />;

    const handleGenerateCode = async (e?: React.FormEvent) => {
        e?.preventDefault();
        setPhoneError('');
        if (!validatePhone(phone)) {
            setPhoneError('Invalid phone number (10-15 digits)');
            return;
        }
        dispatch(clearError());
        try {
            await dispatch(generateCode({ phone, countryCode })).unwrap();
            toast.success(t('auth.codeSentToast'));
            setStep('code');
            setAuthCode('');
            setResendIn(60);
        } catch {
            toast.error(t('auth.sendCodeFailed'));
        }
    };

    const submitLogin = async () => {
        setCodeError('');
        if (!validateAuthCode(authCode)) {
            setCodeError('Invalid code (6 digits required)');
            return;
        }
        dispatch(clearError());
        try {
            const user = await dispatch(login({ phone, countryCode, authCode })).unwrap();
            await dispatch(fetchUserShops(user.usersId)).unwrap();
            toast.success(t('auth.welcome', { name: user.fullname || 'User' }));
            navigate('/shop-select');
        } catch {
            toast.error(t('auth.loginFailed'));
        }
    };

    const handleBack = () => {
        setStep('phone');
        setAuthCode('');
        setCodeError('');
        dispatch(clearError());
    };

    const supportHref = `https://wa.me/${SUPPORT_WHATSAPP.number}?text=${encodeURIComponent(t('support.prefill'))}`;
    const languageOffer = !isIndonesian && (
        <button type="button" className="text-xs font-bold text-ink-accent" onClick={() => changeLanguage('id')}>🇮🇩 Bahasa Indonesia</button>
    );

    const phoneForm = (
        <form onSubmit={handleGenerateCode} className="flex flex-col gap-4">
            <h2 className="hidden text-[28px] font-black text-ink-brand lg:block">{t('auth.login')}</h2>
            {error && <Alert variant="error" message={error} onClose={() => dispatch(clearError())} />}
            <div className="flex flex-col gap-2">
                <label className="text-[13px] font-extrabold text-ink-secondary">{t('auth.phoneNumber')}</label>
                <div className="flex gap-2.5">
                    <label className="relative flex h-[58px] shrink-0 items-center rounded-md bg-surface-sunken px-3.5 text-lg font-extrabold lg:h-[60px] lg:px-4">
                        <select
                            value={countryCode}
                            onChange={(e) => setCountryCode(e.target.value)}
                            className="appearance-none bg-transparent pr-6 text-lg font-extrabold outline-none tnum"
                            aria-label={t('auth.countryCode')}
                        >
                            {COUNTRY_CODES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-3 h-4 w-4 text-ink-muted" />
                    </label>
                    <div className="min-w-0 flex-1">
                    <Input
                        fieldSize="lg"
                        numeric
                        type="tel"
                        inputMode="numeric"
                        placeholder="812 3456 7890"
                        value={phone}
                        onChange={(e) => { setPhone(e.target.value.replace(/\D/g, '')); setPhoneError(''); }}
                        error={phoneError}
                        required
                        autoFocus
                        wrapperClassName="!h-[58px] lg:!h-[60px]"
                    />
                    </div>
                </div>
                <p className="text-[13px] text-ink-muted">{t('auth.otpHelper')}</p>
            </div>
            <Button type="submit" variant="primary" size="lg" fullWidth loading={isLoading}>{t('auth.requestOtp')}</Button>
            <Button variant="ghost" size="md" fullWidth icon={<MessageCircle />} onClick={() => window.open(supportHref, '_blank', 'noopener')}>{t('support.contact')}</Button>
            <div className="flex flex-col items-center gap-2">
                {languageOffer}
                <p className="text-xs text-ink-muted">{t('app.name')} v{APP_VERSION}</p>
            </div>
        </form>
    );

    const codeForm = (
        <form onSubmit={(e) => { e.preventDefault(); submitLogin(); }} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2.5">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-status-success-soft text-success-700"><MessageCircle className="h-6 w-6" /></span>
                <h2 className="text-[32px] font-black leading-tight tracking-[-0.02em] text-ink-brand lg:text-[28px]">{t('auth.checkWhatsApp')}</h2>
                <p className="text-base text-ink-secondary lg:text-[15px]">
                    {t('auth.otpSentTo')} <strong className="font-extrabold text-ink-primary tnum">{formatPhone(phone, countryCode)}</strong>
                </p>
                <p className="text-[15px] font-bold text-ink-primary">{t('auth.verify')} · {t('auth.enterCode')}</p>
            </div>
            {error && <Alert variant="error" message={error} onClose={() => dispatch(clearError())} />}
            <OtpInput value={authCode} onChange={(v) => { setAuthCode(v); setCodeError(''); }} disabled={isLoading} autoFocus className="mt-2" />
            {codeError && <p className="text-xs font-semibold text-status-danger">{codeError}</p>}
            <button type="button" onClick={() => handleGenerateCode()} disabled={isLoading || resendIn > 0} className="flex items-center gap-2 text-sm text-ink-muted disabled:cursor-default">
                <Timer className="h-4 w-4" />
                {resendIn > 0 ? <span>{t('auth.resendIn', { time: mmss(resendIn) })}</span> : <span className="font-extrabold text-ink-accent">{t('auth.resendCode')}</span>}
            </button>
            <div className="flex-1" />
            <Button type="submit" variant="primary" size="lg" fullWidth loading={isLoading} disabled={authCode.length < 6}>{t('auth.verify')}</Button>
            <p className="text-center text-[13px] text-ink-secondary">
                {t('auth.wrongNumber')} <button type="button" onClick={handleBack} className="font-extrabold text-ink-accent">{t('auth.changeNumber')}</button>
            </p>
            <div className="flex justify-center">{languageOffer}</div>
        </form>
    );

    return (
        <div className={`flex min-h-dvh flex-col lg:flex-row ${step === 'phone' ? 'bg-surface-brand' : 'bg-surface-page lg:bg-surface-brand'}`}>
            {/* Hero / message column */}
            <div className={`flex flex-col items-center justify-center gap-[18px] px-8 text-center text-white lg:flex-1 lg:items-start lg:px-20 lg:text-left ${step === 'phone' ? 'flex-1 py-12' : 'hidden lg:flex'}`}>
                <img src="/logo.svg" alt="" draggable={false} className="h-24 w-24 rounded-[24%] shadow-[0_12px_30px_rgba(0,0,0,.3)] lg:h-[104px] lg:w-[104px]" />
                <h1 className="text-[32px] font-black leading-[1.1] tracking-[-0.02em] lg:text-[52px]">{t('auth.welcomeTitle')}</h1>
                <p className="max-w-[420px] text-base text-primary-100 lg:text-[19px]">{t('auth.loginSubtitle')}</p>
            </div>

            {/* Form card */}
            <div className={`flex flex-col lg:m-6 lg:w-[540px] lg:shrink-0 lg:justify-center lg:rounded-[28px] lg:bg-surface-card lg:p-12 lg:shadow-xl ${step === 'phone' ? 'rounded-[32px_32px_0_0] bg-surface-card p-[28px_24px_max(28px,env(safe-area-inset-bottom))]' : 'flex-1 px-6 pb-8'}`}>
                {step === 'code' && (
                    <div className="mb-6 flex lg:mb-4">
                        <IconButton variant="card" label={t('common.back')} onClick={handleBack} className="lg:!bg-surface-sunken lg:!shadow-none"><ArrowLeft /></IconButton>
                    </div>
                )}
                {step === 'phone' ? phoneForm : codeForm}
            </div>
        </div>
    );
};

export default LoginPage;
